/* eslint-disable no-unused-vars */
import React from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Box, Image, Link, Text, Button, Divider
} from 'components/ui';
import Grid from 'components/Grid/Grid';
import Apple from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/Apple/Apple';
import Paypal from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/Paypal/Paypal';
import Klarna from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/Klarna/Klarna';
import Venmo from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/Venmo/Venmo';
import { AfterPayPill } from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/AfterPay';
import Paze from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/Paze/Paze';
import StringUtils from 'utils/String';

/**
 * NewUserPaymentView - Payment section for new users with no saved payment method
 * Shows "Add New Card" button, "Use a Gift Card" link, and "Other Ways to Pay" section as pill buttons
 * @param {Object} props - Component props
 * @param {Object} props.localization - Localized strings
 * @param {Object} props.availablePaymentMethods - Available payment method flags
 * @param {Object} props.paymentOptions - Payment options including defaultPayment
 * @param {Object} props.orderDetails - Order details for Apple Pay
 * @param {string} props.installmentValue - Pre-calculated installment value from parent
 * @param {Function} props.onAddNewCard - Handler for opening credit card form
 * @param {Function} props.handleAfterPayExpand - Callback to expand AfterPay view (navigates to L2/L3 state)
 * @param {boolean} props.disableContainerPadding - Disable container padding for reuse in edit view
 * @param {Function} props.handleKlarnaExpand - Callback to expand Klarna view (navigates to L2/L3 state)
 * ([SHP-3366] Implement the new Klarna flow for fric check rel 4)
 */
const NewUserPaymentView = ({
    localization,
    availablePaymentMethods = {},
    _paymentOptions = {},
    orderDetails = {},
    installmentValue,
    onAddNewCard,
    handleAfterPayExpand,
    disableContainerPadding = false,
    withGiftCard = true,
    handleKlarnaExpand,
    handleVenmoExpand,
    handlePazeExpand,
    handlePayWithApplePayClick,
    handlePayPalClick
}) => {
    const handleUseGiftCard = () => {
        // To be implemented in separate story
    };

    const handlePaymentClick = () => {
        // To be implemented in separate story
    };

    const switchPaymentState = () => {
        // To be implemented in separate story
    };

    const processPaymentClickEvent = () => {
        // To be implemented in separate story
    };

    const {
        isPayPalEnabled,
        isVenmoEnabledForThisOrder,
        isKlarnaEnabledForThisOrder,
        isAfterpayEnabledForThisOrder,
        isAfterpayEnabledForThisProfile,
        isPazeEnabledForThisOrder,
        isPayPalPayLaterEligible
    } = availablePaymentMethods;

    const hasAlternatePayments =
        isPayPalEnabled || isVenmoEnabledForThisOrder || isKlarnaEnabledForThisOrder || isAfterpayEnabledForThisOrder || isPazeEnabledForThisOrder;

    // Build dynamic list of installment payment methods (only PayPal, Klarna, AfterPay offer installments)
    const installmentMethods = [];

    if (isPayPalPayLaterEligible) {
        installmentMethods.push('PayPal');
    }

    if (isKlarnaEnabledForThisOrder) {
        installmentMethods.push('Klarna');
    }

    if (isAfterpayEnabledForThisOrder && isAfterpayEnabledForThisProfile) {
        installmentMethods.push('AfterPay');
    }

    // Format payment method list: "PayPal, Klarna and AfterPay" or "Klarna and AfterPay"
    const getPaymentMethodsList = () => {
        if (installmentMethods.length === 0) {
            return '';
        }

        if (installmentMethods.length === 1) {
            return installmentMethods[0];
        }

        if (installmentMethods.length === 2) {
            return `${installmentMethods[0]} and ${installmentMethods[1]}`;
        }

        return `${installmentMethods.slice(0, -1).join(', ')} and ${installmentMethods[installmentMethods.length - 1]}`;
    };

    // Show installment message only if we have installment value and at least one installment method
    const showInstallmentMessage = installmentValue && installmentMethods.length > 0;

    return (
        <Box
            paddingX={disableContainerPadding ? 0 : [4, 4, 5]}
            paddingTop={disableContainerPadding ? 0 : [4, 5]}
            paddingBottom={disableContainerPadding ? 0 : [1, 3]}
            data-at={Sephora.debug.dataAt('new_user_payment_view')}
        >
            {/* Add New Card Button */}
            {onAddNewCard && (
                <Button
                    variant='primary'
                    width={['297px', '297px', '50%']}
                    justifyContent='center'
                    onClick={onAddNewCard}
                    aria-label={localization.addNewCard}
                    data-at={Sephora.debug.dataAt('add_new_card_button')}
                    children={localization.addNewCard}
                />
            )}

            {withGiftCard && (
                <>
                    {/* Divider before Gift Card */}
                    <Divider marginY={4} />

                    {/* Use a Gift Card Link */}
                    <Link
                        display='flex'
                        alignItems='center'
                        gap={3}
                        onClick={handleUseGiftCard}
                        aria-label={localization.useGiftCard}
                        data-at={Sephora.debug.dataAt('use_gift_card_link')}
                        role='button'
                        tabIndex={0}
                        onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleUseGiftCard();
                            }
                        }}
                    >
                        <Image
                            src='/img/ufe/payments/giftCardBordered.svg'
                            display='block'
                            width={25}
                            height={16}
                            alt=''
                            aria-hidden='true'
                            marginRight={2}
                        />
                        <Text
                            color='blue'
                            fontSize='base'
                        >
                            {localization.useGiftCard}
                        </Text>
                    </Link>

                    {/* Divider after Gift Card */}
                    <Divider marginY={[3, 4]} />
                </>
            )}

            {/* Other Ways to Pay Section - Pill Button Layout */}
            {hasAlternatePayments && (
                <Box>
                    <Text
                        is='h3'
                        fontWeight='bold'
                        marginBottom={[1, 3]}
                        aria-level='3'
                    >
                        {localization.otherWaysToPay}
                    </Text>

                    {/* Interest-free payments description - dynamic based on order amount and available methods */}
                    {showInstallmentMessage && (
                        <Text
                            is='p'
                            lineHeight={['18px', 'none']}
                        >
                            {localization.payWith}
                            <b>{` ${StringUtils.format(localization.installmentsMessage, installmentValue)} `}</b>
                            {`${localization.availableWith} `}
                            {getPaymentMethodsList()}
                        </Text>
                    )}

                    {/* Payment Methods - Using Real UFE Components */}
                    <Grid
                        columns={[2, null, 4]}
                        rowGap={4}
                        columnGap={[2, 4]}
                        marginTop={[2, null, 2]}
                        role='group'
                        aria-label='Alternative payment methods'
                    >
                        <Apple
                            isPayWithApplePay={false}
                            handlePayWithApplePayClick={handlePayWithApplePayClick}
                            orderDetails={orderDetails}
                        />
                        <Paypal
                            isPayPalEnabled={isPayPalEnabled}
                            handlePaymentClick={handlePayPalClick}
                        />
                        <Klarna
                            isKlarnaEnabledForThisOrder={isKlarnaEnabledForThisOrder}
                            handlePaymentClick={handleKlarnaExpand}
                        />
                        <AfterPayPill
                            isAfterpayEnabledForThisOrder={isAfterpayEnabledForThisOrder}
                            isAfterpayEnabledForThisProfile={isAfterpayEnabledForThisProfile}
                            handleAfterPayExpand={handleAfterPayExpand}
                        />
                        <Venmo
                            isVenmoEnabledForThisOrder={isVenmoEnabledForThisOrder}
                            handleVenmoExpand={handleVenmoExpand}
                        />
                        <Paze
                            isPazeEnabledForThisOrder={isPazeEnabledForThisOrder}
                            handlePaymentClick={handlePazeExpand}
                        />
                    </Grid>
                </Box>
            )}
        </Box>
    );
};

export default wrapFunctionalComponent(NewUserPaymentView, 'NewUserPaymentView');
